import axios, { AxiosPromise, AxiosResponse } from "axios";
import {
  Paginated,
  Pagination,
  PaginationQuery,
  convertToUrlParams,
} from "./common";
import { type LinkPropertyValue, TwinResponse } from "./twinManagement";

export type PropertyValue = {
  twinUuid: string;
  propertyUuid: string;
  value: string | number;
  processorBindingUuid?: string;
  scenario?: string;
  timestamp?: string;
  metadata?: object;
};

export async function addPropertyValue(value: PropertyValue) {
  return axios.post("/api/data-store/data/", value);
}

export type TwinPropertyValues = {
  comment?: string;
  longComment?: string;
  twinName?: string;
  twinDescription?: string;
  twinType?: string;
  twinActive?: boolean;
  properties: Array<{
    propertyUuid: string;
    value: string | number | Array<string> | LinkPropertyValue | null;
    scenario?: string;
    metadata?: object;
  }>;
};

export async function addTwinPropertyValues(
  twinId: string,
  payload: TwinPropertyValues,
) {
  return axios.post(`/api/data-store/twins/${twinId}`, payload);
}

export async function setPropertyByCustomId(
  twin: TwinResponse,
  customId: string,
  value: any,
  comment: string,
  longComment: string,
) {
  const propertyUuid = twin?.properties.find(
    (item) => item.customId === customId,
  )?.uuid;
  if (propertyUuid === undefined) {
    throw new Error("Try to set unknown property " + customId);
  }
  await addTwinPropertyValues(twin.uuid, {
    comment,
    longComment,
    properties: [
      {
        propertyUuid,
        value,
      },
    ],
  });
}

export type TwinChangelogQuery = {
  twins?: string[];
  names?: string[];
  changeBy?: string[];
  type?: string;
  startDate?: string;
  endDate?: string;
};

export type ChangeObj =
  | {
      type: "UPDATE";
      key: string;
      value?: string | number;
      oldValue?: string | number;
      changes: Array<ChangeObj>;
    }
  | {
      type: "ADD";
      key: string;
      value?: ChangeObj;
      oldValue?: ChangeObj;
      changes: Array<ChangeObj>;
    };

export type TwinChangelog = {
  twinUuid: string;
  changedAt: string;
  changedBy: object;
  comment: string;
  snapshotAfter: TwinResponse;
  changeType: "NEW" | "UPDATE";
  diff: {
    changes: Array<ChangeObj>;
  };
};

export function getTwinChangelog(
  paging: PaginationQuery,
  request: TwinChangelogQuery,
): AxiosPromise<Paginated<TwinChangelog[]>> {
  return axios.post(
    `/api/changelogs/twins/search/?${convertToUrlParams(paging)}`,
    request,
  );
}

// Value lists
export type ValueListMetaData = {
  name: string;
  customId: string;
  description: string;
  type: string;
  filterId?: string;
  uuid?: string;
  created?: string;
  modified?: string;
};

export function postValueList(valueList: ValueListMetaData) {
  return axios.post(`/api/value-lists`, valueList);
}

export function patchValuesList(id: string, patch: Partial<ValueListMetaData>) {
  return axios.patch(`/api/value-lists/${id}`, patch);
}

export function getValueListsMetaData(): AxiosPromise<
  Paginated<ValueListMetaData[]>
> {
  return axios.get(`/api/value-lists`);
}

export function deleteValueList(id: string) {
  return axios.delete(`/api/value-lists/${id}`);
}

export type ValueListItem = {
  uuid?: string;
  name: string;
  value: string;
  valueListId: string;
  filterValue?: string;
};

export function getValueListItems(
  valueListId: string,
): AxiosPromise<Paginated<ValueListItem[]>> {
  return axios.get(
    `/api/value-list-items?${new URLSearchParams({ valueListId })}`,
  );
}

export function addValueListItem(item: ValueListItem) {
  return axios.post(`/api/value-list-items`, item);
}

export function deleteValueListItem(id: string) {
  return axios.delete(`/api/value-list-items/${id}`);
}
